import { ReactNode, useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { hasTabAccess } from "@/lib/adminPermissionMap";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "./ui/button";
import { savePendingDeepLink } from "@/lib/notifications/pendingDeepLink";
import { SocialsAuthFragmentBridge } from "@/components/socials/SocialsAuthFragmentBridge";

/**
 * Protects /socials/* with plain Supabase auth, then allows either HIBANE
 * Desk users with social-media module access OR tenant users with an active
 * socials_org_members row. Tenant users deliberately do not need an
 * admin_sessions row.
 *
 * Mirrors VoiceProtectedRoute — perpetual session with a 15s cold-start
 * grace loop (auth listener + poll + one silent refresh); only explicit
 * Sign Out ends a tenant session.
 */
export default function SocialsProtectedRoute({ children }: { children: ReactNode }) {
  return (
    <SocialsAuthFragmentBridge>
      <SocialsProtectedRouteInner>{children}</SocialsProtectedRouteInner>
    </SocialsAuthFragmentBridge>
  );
}

function SocialsProtectedRouteInner({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, loading } = useAdminAuth();
  const [rehydrating, setRehydrating] = useState(false);
  const [rehydrateFailed, setRehydrateFailed] = useState(false);

  if (location.pathname.startsWith("/socials") && location.pathname !== "/socials/login") {
    savePendingDeepLink("socials", location.pathname + location.search);
  }

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      if (
        sess?.user &&
        (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION")
      ) {
        setRehydrateFailed(false);
        setRehydrating(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const hasPersistedSupabaseSession = useCallback(() => {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
          const raw = localStorage.getItem(k);
          if (raw && raw.length > 10) return true;
        }
      }
    } catch { /* noop */ }
    return false;
  }, []);

  useEffect(() => {
    if (loading || user) {
      if (user && rehydrateFailed) setRehydrateFailed(false);
      return;
    }
    if (rehydrating || rehydrateFailed) return;

    if (!hasPersistedSupabaseSession()) {
      setRehydrateFailed(true);
      return;
    }

    setRehydrating(true);
    let cancelled = false;
    const start = Date.now();
    const graceMs = 15000;
    let refreshTried = false;

    const poll = async () => {
      while (!cancelled) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          cancelled = true;
          setRehydrating(false);
          return;
        }
        if (!refreshTried && Date.now() - start > 400) {
          refreshTried = true;
          await supabase.auth.refreshSession().catch(() => {});
        }
        const elapsed = Date.now() - start;
        await new Promise((r) => setTimeout(r, elapsed < 3000 ? 250 : 500));

        if (Date.now() - start >= graceMs) {
          setRehydrating(false);
          setRehydrateFailed(true);
          return;
        }
      }
    };
    void poll();

    return () => { cancelled = true; };
  }, [loading, user, rehydrating, rehydrateFailed, hasPersistedSupabaseSession]);

  if (loading || rehydrating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user && rehydrateFailed) {
    return <Navigate to="/socials/login" replace />;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return <SocialsAccessGate>{children}</SocialsAccessGate>;
}

function SocialsAccessGate({ children }: { children: ReactNode }) {
  const { isSuperAdmin, permissions, loading, initialized } = useAdminPermissions();
  const { user, isAdmin, clearAdminSession } = useAdminAuth();

  // React-query-backed tenant membership check so mutations on
  // socials_org_members invalidate ["socials-access"] and the UI re-evaluates
  // without a reload.
  const { data: orgMemberCheck, isLoading: memberLoading } = useQuery({
    queryKey: ["socials-access", user?.id ?? null],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<"member" | "none"> => {
      if (!user?.id) return "none";
      const { data, error } = await supabase
        .from("socials_org_members")
        .select("id")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .limit(1);
      if (error) return "none";
      return data && data.length > 0 ? "member" : "none";
    },
  });

  const waitingForDeskPermissions = isAdmin && (loading || !initialized);
  if (waitingForDeskPermissions || memberLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const allowedAsDesk = hasTabAccess("social-media", isSuperAdmin, permissions);
  const allowedAsOrgMember = orgMemberCheck === "member";
  if (allowedAsDesk || allowedAsOrgMember) return <>{children}</>;

  const signOut = async () => {
    try { await supabase.auth.signOut(); } catch { /* noop */ }
    clearAdminSession();
    window.location.replace("/socials/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full rounded-2xl border bg-card p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-foreground">No Social Studio access</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Your account ({user?.email}) is signed in, but it has no Social
          Studio permissions and no tenant organization membership. Ask a
          HIBANE administrator or your organization owner to grant you access.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={signOut} variant="default" className="w-full">Sign out</Button>
          <Button asChild variant="outline" className="w-full">
            <a href="/admin">Go to HIBANE Desk</a>
          </Button>
        </div>
      </div>
    </div>
  );
}

export { SocialsProtectedRoute };
